import Vue from 'vue';
import { BfModal, BfDialog, BfButton, BfTextInput } from 'src/components/Butterfly';
import { FIELD_TYPES_INDEX } from "components/project/create/SchemaUtils";
import Utils from 'src/utils/general';
import _ from 'lodash';
import Papa from 'papaparse';
// maximum number of rows in transformations file upload
const FILE_UPLOAD_LIMIT = 1000;
export default Vue.extend({
    components: { BfModal, BfDialog, BfButton, BfTextInput },
    props: {
        visible: { type: Boolean, default: false, required: false },
        fields: { type: Array, default: () => { return []; }, required: false },
        customColumns: { type: Array, required: true },
    },
    data() {
        return {
            referenceField: this.fields[0] && this.fields[0].index,
            newName: '',
            newType: null,
            transformations: [{ id: Utils.uuid() }],
            errors: [],
            page: 1,
            isLoading: false,
            columnId: null,
        };
    },
    computed: {
        buttonText() {
            if (this.page === undefined) {
                return '';
            }
            else if (this.page === 1) {
                return 'Next';
            }
            else if (this.page === 2) {
                return 'Done';
            }
            else {
                throw new Error(`Invalid page: ${this.page}. Valid values are 1 and 2.`);
            }
        },
        header() {
            if (this.page === 3) {
                return 'Edit Custom Columns';
            }
            let h = `${this.columnId ? 'Update' : 'Add'} Custom Column`;
            if (this.page === 2) {
                h = `${h} - ${this.newName}`;
            }
            return h;
        }
    },
    methods: {
        resetState() {
            this.transformations = [{ id: Utils.uuid() }];
            this.errors = [];
            this.referenceField = this.fields[0].index;
            this.newName = '';
            this.page = 1;
            this.newType = null;
            this.columnId = null;
        },
        close() {
            this.resetState();
            this.$emit('close');
        },
        addRow() {
            this.transformations.push({ id: Utils.uuid() });
            this.errors = [];
        },
        clearTransformations() {
            this.transformations = [{ id: Utils.uuid() }];
            this.errors = [];
        },
        uploadTransformationsFile() {
            this.$refs.upload.click();
            this.errors = [];
        },
        processTransformationsFile(e) {
            const file = e.target.files[0];
            const reader = new window.FileReader();
            reader.onload = (readEvent) => {
                //let transformations = processStopwords(readEvent.target.result)
                const results = Papa.parse(readEvent.target.result, { skipEmptyLines: true });
                // Check number of transformations
                if (results.data.length > FILE_UPLOAD_LIMIT) {
                    this.errors.push(`Uploaded file has ${results.data.length} rows which is more than the ${FILE_UPLOAD_LIMIT} limit`);
                }
                else {
                    // Check shape of transformations
                    for (var i = 0; i < results.data.length; i++) {
                        let d = results.data[i];
                        if (d.length !== 2) {
                            this.errors.push(`Row ${i + 1} has ${d.length} values, but exactly 2 are required`);
                            break;
                        }
                    }
                }
                if (this.errors.length === 0) {
                    // Add the transformations
                    results.data.forEach(d => {
                        const firstRow = this.transformations[0];
                        // Update empty first row
                        if (this.transformations.length === 1 && !firstRow.from && !firstRow.to) {
                            this.transformations[0].from = d[0];
                            this.transformations[0].to = d[1];
                        }
                        else {
                            this.transformations.push({
                                id: Utils.uuid(),
                                from: d[0],
                                to: d[1]
                            });
                        }
                    });
                }
                e.target.value = null;
                this.$analytics.track.project.uploadColumnTransformations(this.fields[this.referenceField].name, this.newName, FIELD_TYPES_INDEX[this.newType].label, results.data.length);
            };
            reader.readAsText(file);
        },
        // Edit the transformations for an existing custom column
        editCustomColumn(item) {
            this.referenceField = this.fields.find(f => f.name === item.reference_field).index;
            this.newName = item.name;
            this.type = item.type;
            this.transformations = item.transformations;
            this.page = 2;
            this.columnId = item.id;
        },
        // Enter or leave the page to view/edit existing custom columns
        editCustomColumns() {
            if (this.page === 3) {
                this.resetState();
                this.page = 1;
            }
            else {
                this.page = 3;
            }
        },
        back() {
            this.errors = [];
            if (this.columnId) {
                this.page = 3;
            }
            else {
                this.page = 1;
            }
        },
        next() {
            this.errors = [];
            this.isLoading = true;
            try {
                if (this.page === 1) {
                    this.validateFieldValues();
                    if (this.errors.length > 0) {
                        return;
                    }
                    this.page++;
                }
                else if (this.page === 2) {
                    this.validateTransformations();
                    if (this.errors.length > 0) {
                        return;
                    }
                    // Semantic UI won't let me use the field object in the dropdown. So
                    // we have to use the index of the selected field everywhere and then
                    // emit the full schema object at this point.
                    const schemaField = this.fields.find(x => x.index === this.referenceField);
                    this.$emit('add-or-update', {
                        id: this.columnId || Utils.uuid(),
                        transformations: this.transformations,
                        referenceField: schemaField,
                        newName: this.newName,
                        type: this.newType,
                    });
                    this.close();
                }
                else {
                    throw new Error(`Invalid page ${this.page}. Valid values are 1 and 2.`);
                }
            }
            finally {
                this.isLoading = false;
            }
        },
        validateFieldValues() {
            const fieldNames = this.fields.map(f => f.name);
            const fieldIdxs = this.fields.map(f => f.index);
            if (!this.newName) {
                this.errors.push(`New column header must not be empty`);
            }
            else if (this.newName.toUpperCase() === 'NPS CATEGORY') {
                this.errors.push("'NPS Category' is a reserved column header & cannot be used in imported data");
            }
            else if (fieldNames.indexOf(this.newName) !== -1) {
                this.errors.push(`New column header: ${this.newName} already exists in the dataset`);
            }
            if (!this.newType) {
                this.errors.push(`New column type must not be empty.`);
            }
            if (this.referenceField == null) {
                this.errors.push(`Source column must not be empty`);
            }
            else if (fieldIdxs.indexOf(this.referenceField) === -1) {
                this.errors.push(`Source column could not be found in the original dataset`);
            }
        },
        validateTransformations() {
            for (const item of this.transformations) {
                const from = item.from?.trim();
                if (from === undefined) {
                    this.errors.push(`Missing source value. Make sure there are no empty rows.`);
                }
            }
            const allFroms = this.transformations.map(item => item.from);
            const uniqueFroms = _.uniq(allFroms);
            if (allFroms.length !== uniqueFroms.length) {
                const delta = allFroms.length - uniqueFroms.length;
                this.errors.push(`${delta} duplicated source values`);
            }
        },
        removeTransformation(id) {
            this.transformations = this.transformations.filter(x => x.id !== id);
        },
        removeCustomColumn(id) {
            this.$emit('remove', id);
        }
    }
});
